import Query from './Query'
import DataFetcher from './DataFetcher'
import QueryResult from './QueryResult'

/**
 * @constructor
 * @param {SearcherReg[]} searcherRegs An array of {@link Controller.SearcherReg}
 * @param {Object} [options]
 * @param [options.onError] {Controller~errorCallback}
 * @param [options.blankBehavior="none"] {string} "none"|"search" Determines whether the controller issues an empty querystring to its searchers
 * @example
 * var controller = new Controller(
 *   searcherRegs, // Previously created array of searcherRegs
 *   {onError : function(searcher, errorThrown) { alert('Error: '+ searcher.getId() + ': '+ errorThrown); },
 *    blankBehavior: "search"
 *   }
 *  );
 * controller.go();
 * controller.addSearcher(searcherReg); // Additional searchers may be added to the controller after go();
 *
 * */
export default function Controller(searcherRegs, options) {
  this.idSerial = 0;
  const searchersArg = searcherRegs; // [{"searcher" : new FeatureSearcher(featureSearchOptions)},]
  //Vars
  this.searchers = [];

  this.activeDataFetcher = null;
  this.onErrorHandlers = [];
  this.onAddSearcherHandlers = [];
  this.onSelectHandlers = [];
  this.onInspectHandlers = [];
  this.onPeakHandlers = [];

  this.blankBehavior = "none"; //none, search
  //Options
  if (options !== null) {
    if (typeof options.onError === "function") {
      this.addErrorHandler(options.onError);
    }
    if (options.blankBehavior) {
      this.blankBehavior = options.blankBehavior;
    }
  }
  this.allSearchersProcessed = Promise.resolve('success');
  for (let i = 0; i < searchersArg.length; i++) {
    this.addSearcher(searchersArg[i]);
  }
};

//Peak related stuff
/**
 * register an onPeak handler.
 * @param {function} callback A function which is called when a result is hovered upon. callback is called with {Result} or Null on Hover end
 */
Controller.prototype.addOnPeakHandler = function (callback) {
  this.onPeakHandlers.push(callback);
};

Controller.prototype.onPeak = function (result) {
  for (let i = 0; i < this.onPeakHandlers.length; i++) {
    this.onPeakHandlers[i](result);
  }
};

//Inspect related stuff
//noinspection JSCommentMatchesSignature,JSCommentMatchesSignature
/**
 * register an onInspect handler.
 * @callback A function which is called when a result is selected for inspection
 * @param {Result}
 */
Controller.prototype.addOnInspectHandler = function (callback) {
  this.onInspectHandlers.push(callback);
};

Controller.prototype.onInspect = function (result) {
  for (let i = 0; i < this.onInspectHandlers.length; i++) {
    this.onInspectHandlers[i](result);
  }
};

//Select related stuff
//noinspection JSCommentMatchesSignature,JSCommentMatchesSignature
/**
 * register an onSelect handler.
 * @callback A function which is called when a result is selected
 * @param {Result}
 */
Controller.prototype.addOnSelectHandler = function (callback) {
  this.onSelectHandlers.push(callback);
};

Controller.prototype.onSelect = async function (result, view) {
  if (typeof result !== 'undefined') {
    if (result.hasOwnProperty("newquery") && result.newquery !== null) {
      this.callSelectHandlers(result);
      setTimeout(() => view.doTextSearch(result.newquery, result.source, result.type), 50);
    } else {
      if (result.searcher) {
        //New code
        let completedResult = await result.searcher.completeResult(result);
        this.callSelectHandlers(completedResult);
        if (result.searcher.hasdetailHandlerDefs(completedResult)) {
          view.doDetail(completedResult);
        }
      }
    }
  }
};

Controller.prototype.callSelectHandlers = function (result) {
  if (result.searcher) {
    result.searcher._onSelect(result);
  }
  for (let i = 0; i < this.onSelectHandlers.length; i++) {
    this.onSelectHandlers[i](result);
  }
};

//Error related stuff

/**
 * register an error handler.
 * @param {function} callback A function which is called when an error is thrown in a searcher
 */
Controller.prototype.addErrorHandler = function (callback) {
  this.onErrorHandlers.push(callback);
};

/**
 * call error handlers
 */
Controller.prototype.onError = function (searcher, errorThrown) {
  for (let i = 0; i < this.onErrorHandlers.length; i++) {
    this.onErrorHandlers[i](searcher, errorThrown);
  }
};

//Add searcher related stuff
/**
 * Add a searcher
 * @param {Controller.SearcherReg} searcherReg
 */
Controller.prototype.addSearcher = function (searcherReg) {
  const searcher = searcherReg.searcher;
  searcher.id = this.idSerial++;
  this.searchers.push(searcher);
  this.allSearchersProcessed = Promise.all([this.allSearchersProcessed, this.thisSearcherProcessed(searcher)]);
};

Controller.prototype.thisSearcherProcessed = async function(searcher){
  try{
    await searcher.ready();
    this.onAddSearcher();
  }catch(e){
    this.removeSearcher(searcher);
  }
};

Controller.prototype.removeSearcher = function (searcher) {
  for (let i = 0; i < this.searchers.length; i++) {
    let thisSearcher = this.searchers[i];
    if (thisSearcher.id === searcher.id) {
      this.searchers.splice(i, 1);
      break;
    }
  }
};

Controller.prototype.addOnAddSearcherHandler = function (callback) {
  this.onAddSearcherHandlers.push(callback);
};

/**
 * call addSearcher handlers
 */
Controller.prototype.onAddSearcher = function () {
  for (let i = 0; i < this.onAddSearcherHandlers.length; i++) {
    this.onAddSearcherHandlers[i]();
  }
};

/**
 * @private
 */
Controller.prototype.go = function () {
  throw ("controller.go is outdated.");
};

/**
 * A searcher registration.
 * @typedef {Object} Controller.SearcherReg
 * @property {Searcher} searcher An implementation of {@link Searcher}
 */

//noinspection JSCommentMatchesSignature,JSCommentMatchesSignature,JSCommentMatchesSignature,JSCommentMatchesSignature,JSCommentMatchesSignature
/**
 * Called by the view
 * @private
 * @param {string} searcherId 1
 * @param {Query} callQuery
 * @param {function} completeCallback
 */
Controller.prototype.fetchData = function (queryString, completeCallback, limit, timeout, source, type) {
  //search, none
  let queryBuilder = Query.Builder().queryString(queryString).limit(limit).timeout(timeout).type("list");
  if (source) {
    queryBuilder.target({
      source,
      type: type ? type : null
    });
  }
  const query = queryBuilder.build();
  if ((query.queryString.length > 0) || (query.queryString.length === 0 && this.blankBehavior === "search")) {
    return this.fetchDataFromQueryObject(query, completeCallback);
  } else {
    const queryResult = new QueryResult(null);
    completeCallback(queryResult.getAllResults(), true);
    return Promise.resolve(queryResult.getAllResults());
  }

};

//noinspection JSCommentMatchesSignature
/**
 * Initiates a query
 * @private
 *
 * @param {string} searcherId
 * @param {Query} callQuery
 * @param {function} completeCallback
 */
Controller.prototype.fetchDataFromQueryObject = function (callQuery, completeCallback) {
  //Is there an active DataFetcher? then cancel it
  if (this.activeDataFetcher !== null) {
    this.activeDataFetcher.cancel();
    if (this.activeDataFetcher.completeCallback) {
      this.activeDataFetcher.completeCallback([], true);
    }
    this.activeDataFetcher = null;
  }

  let searchersToFetchFrom = this.getTargetedSearchersFromQuery(callQuery);

  let fetchQueryBuilder = Query.Builder().copy(callQuery);

  if (searchersToFetchFrom.length == 1) {
    //fetchQueryBuilder = fetchQueryBuilder.type("list.force").limit(100);
    let searcher = searchersToFetchFrom[0];
    let searcherSources = searcher.getSources();
    let searcherHasOnlyOneType = (searcherSources.length === 1 && searcherSources[0].types.length === 1);
    if (callQuery.hasTarget || searcherHasOnlyOneType) {
      fetchQueryBuilder = fetchQueryBuilder.type("list.force").limit(100);
    } else {
      fetchQueryBuilder = fetchQueryBuilder.type("list").limit(Math.round(callQuery.limit));
    }
  } else {
    fetchQueryBuilder = fetchQueryBuilder.type("list").limit(Math.round(callQuery.limit / searchersToFetchFrom.length));
  }

  const fetchQuery = fetchQueryBuilder.build();

  this.activeDataFetcher = new DataFetcher(this, searchersToFetchFrom);
  if (completeCallback !== null && typeof completeCallback === "function") {
    this.activeDataFetcher.completeCallback = completeCallback;
  }
  return this.activeDataFetcher.fetch(fetchQuery);
};

Controller.prototype.get = async function (source, type, id) {
  let y = await this.allSearchersProcessed;
  if (y === null){
    y = 2;
  }
  let searcher = this.getSearcherBySourceType(source, type);
  if (searcher === null) {
    throw (new Error("Controller.get: could not find searcher"));
  } else {
    let y2 = await searcher.ready();
    if (y2 === null){
      y2 = 2;
    }
    let result = await searcher.get(id, type, source);
    if (result === null){
      throw (new Error("Controller.get: result === null"));
    }
    let completeResult = await result.searcher.completeResult(result);
    if (completeResult === null){
      throw (new Error("Controller.get: completeResult === null"));
    }
    return completeResult;
  }
};

Controller.prototype.fetchDataCallback = function (dataFetcher, queryResult, isLast) {
  if (dataFetcher === this.activeDataFetcher) {
    if (typeof dataFetcher.completeCallback === "function") {
      dataFetcher.completeCallback(queryResult, isLast);
    }
    if (isLast) {
      this.activeDataFetcher = null;
    }
  }
};

Controller.prototype.getSearcherSuggestions = function (queryString) {
  const queryResult = new QueryResult(null);
  for (let i = 0; i < this.searchers.length; i++) {
    const searcher = this.searchers[i];
    searcher.getSuggestions(queryResult, queryString);
  }
  return queryResult;
};

Controller.prototype.cancelFetches = function () {
  //Is there an active DataFetcher? then cancel it
  if (this.activeDataFetcher !== null) {
    this.activeDataFetcher.cancel();
    this.activeDataFetcher.completeCallback([], true);
    this.activeDataFetcher = null;
  }
};

//Private functions
Controller.prototype.getTargetedSearchersFromQuery = function (query) {
  if (query.hasTarget) {
    const source = query.target.source.toLowerCase();
    const type = query.target.type.toLowerCase();
    var searcher = this.getSearcherBySourceType(source, type);
    if (searcher !== null) {
      return [searcher];
    } else {
      return [];
    }
  } else {
    return this.searchers;
  }
};

Controller.prototype.getSearcherBySourceType = function (source, type) {
  for (let i = 0; i < this.searchers.length; i++) {
    let thisSearcher = this.searchers[i];
    if (thisSearcher.hasSource(source) && thisSearcher.hasType(type)) {
      return thisSearcher;
    }
  }
  return null;
};

Controller.prototype.getSources = function () {
  let sources = [];
  let getSource = function (sourceName) {
    for (let i = 0; i < sources.length; i++) {
      if (sources[i].source.toLowerCase() === sourceName.toLowerCase()) {
        return sources[i];
      }
    }
    //Else create the source
    let source = {source: sourceName, types: []};
    sources.push(source);
    return source;
  };

  for (let i = 0; i < this.searchers.length; i++) {
    let thisSearcher = this.searchers[i];
    let thisSearcherSources = thisSearcher.getSources();
    for (let j = 0; j < thisSearcherSources.length; j++) {
      let thisSearcherSource = thisSearcherSources[j];
      let source = getSource(thisSearcherSource.source);
      source.types = source.types.concat(thisSearcherSource.types);
    }
  }
  return (sources);
};

Controller.prototype.getSearchers = function () {
  return this.searchers;
};

Controller.prototype.getSearcherFromId = function (id) {
  for (let i = 0; i < this.searchers.length; i++) {
    if (this.searchers[i].getId() == id) {
      return this.searchers[i];
    }
  }
  return null;
};

Controller.prototype.fetchError = function (dataFetcher, searcher, errorThrown) {
  if (dataFetcher === this.activeDataFetcher) {
    this.onError(searcher, errorThrown);
  }
};
